import { useState, useEffect, useContext } from 'react';
import { ApiFetchCachingContext } from 'react-scale/corp/lib/ApiFetchCachingContext';
import { GetRequestHook } from './useGetRequest';
/**
 * Sends a get request to the api server using `apiFetch`. Returns { response, loading, errored }.
 * See also: `useAsync` from react-use
 * Within a single page load, calls to the same url will return the same result (see ApiFetchCachingContext for caching)
 *
 * @param url first argument to `fetch`
 */

export const useApiFetchGetRequest = <T extends object = any>(
  url: string,
  opts?: {
    shouldShortCircuit?: boolean;
    skipCache?: boolean;
    refetchState?: any;
  },
): GetRequestHook<T> => {
  const { get, cacheVersion } = useContext(ApiFetchCachingContext);
  const [state, setState] = useState<GetRequestHook<T>>({
    response: undefined,
    loading: true,
    errored: false,
    statusCode: null,
  });

  useEffect(() => {
    if (opts?.shouldShortCircuit) {
      return;
    }
    setState({
      response: undefined,
      loading: true,
      errored: false,
    });

    let canceled = false;
    const startDate = Date.now();
    get<T>(url, { skipCache: opts?.skipCache })
      .then(response => {
        if (canceled) return;
        const timeMs = Date.now() - startDate;

        setState({
          response,
          timeMs,
          loading: false,
          errored: false,
        });
        return;
      })
      .catch(e => {
        if (canceled) return;
        // eslint-disable-next-line no-console
        console.error('failed to fetch', url, e);
        setState({
          response: undefined,
          loading: false,
          errored: true,
          statusCode: e.status,
        });
      });
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, cacheVersion, opts?.refetchState]);
  return state;
};
